/**
 * Label placement for the guide map's pins.
 *
 * A city guide's hotels, beaches and cafés are often a few hundred metres apart,
 * so at any zoom that keeps the whole guide in view their pins land on top of
 * one another and hide both the icon and the price. This module solves that the
 * way a printed map does: every pin gets a rectangular footprint in screen
 * pixels, and overlapping footprints are pushed apart along whichever axis needs
 * the smaller nudge, repeatedly, until nothing overlaps.
 *
 * The nudge is capped, so a pin never wanders away from the place it marks — the
 * map draws a hairline from the nudged pin back to its exact position.
 *
 * Pure geometry on purpose: no Leaflet and no DOM, so it is unit-testable.
 */

/** A point in the map's pixel space at a given zoom. */
export type PixelPoint = {
  x: number;
  y: number;
};

export type PinFootprint = {
  /** Identifies the pin for stable ordering and for the caller's own bookkeeping. */
  key: string;
  /** Where the pin truly belongs. Never modified. */
  base: PixelPoint;
  /** Where the pin will be drawn. Moved by the layout pass. */
  point: PixelPoint;
  halfWidth: number;
  halfHeight: number;
};

/** Diameter of the round pin head. */
export const PIN_CIRCLE_SIZE = 36;

/** Pin head plus the price pill that hangs under it. */
export const PIN_STACK_HEIGHT = 62;

/** Breathing room kept between two footprints. */
export const PIN_GAP = 6;

/** How far a pin may be nudged from its true position before we stop pushing. */
export const PIN_MAX_OFFSET = 88;

/** Enough passes to settle a full guide; the loop exits as soon as it is calm. */
const PIN_LAYOUT_PASSES = 90;

/** Below this the pin is effectively on its spot and needs no leader line. */
export const PIN_LEADER_THRESHOLD = 4;

/**
 * Width of a pin including its price pill. The pill is 11px semibold text with
 * 8px of padding each side; six pixels per character is a close enough estimate
 * for collision purposes and avoids measuring text in the DOM.
 */
export function estimatePinWidth(priceTag: string): number {
  if (!priceTag) {
    return PIN_CIRCLE_SIZE;
  }
  return Math.min(150, Math.max(PIN_CIRCLE_SIZE, priceTag.length * 6.2 + 20));
}

function clampToOffset(footprint: PinFootprint): void {
  const dx = footprint.point.x - footprint.base.x;
  const dy = footprint.point.y - footprint.base.y;
  const distance = Math.hypot(dx, dy);
  if (distance <= PIN_MAX_OFFSET || distance === 0) {
    return;
  }
  const scale = PIN_MAX_OFFSET / distance;
  footprint.point.x = footprint.base.x + dx * scale;
  footprint.point.y = footprint.base.y + dy * scale;
}

/**
 * Two pins sharing the exact same coordinates have no direction to separate
 * along, so the first of the pair is given a deterministic sideways bias.
 */
function breakTies(footprints: PinFootprint[]): void {
  for (let index = 0; index < footprints.length; index += 1) {
    const current = footprints[index] as PinFootprint;
    for (let other = 0; other < index; other += 1) {
      const earlier = footprints[other] as PinFootprint;
      if (current.point.x === earlier.point.x && current.point.y === earlier.point.y) {
        current.point.x += 0.5 + index * 0.1;
        current.point.y += 0.5;
        break;
      }
    }
  }
}

/** Pushes overlapping pins apart in place. */
export function separatePins(footprints: PinFootprint[]): void {
  breakTies(footprints);
  for (let pass = 0; pass < PIN_LAYOUT_PASSES; pass += 1) {
    let moved = false;
    for (let a = 0; a < footprints.length; a += 1) {
      for (let b = a + 1; b < footprints.length; b += 1) {
        const first = footprints[a] as PinFootprint;
        const second = footprints[b] as PinFootprint;
        const minimumX = first.halfWidth + second.halfWidth + PIN_GAP;
        const minimumY = first.halfHeight + second.halfHeight + PIN_GAP;
        const dx = second.point.x - first.point.x;
        const dy = second.point.y - first.point.y;
        const overlapX = minimumX - Math.abs(dx);
        const overlapY = minimumY - Math.abs(dy);
        if (overlapX <= 0 || overlapY <= 0) {
          continue;
        }
        moved = true;
        if (overlapX <= overlapY) {
          const push = (overlapX / 2) * (dx < 0 ? -1 : 1);
          first.point.x -= push;
          second.point.x += push;
        } else {
          const push = (overlapY / 2) * (dy < 0 ? -1 : 1);
          first.point.y -= push;
          second.point.y += push;
        }
        clampToOffset(first);
        clampToOffset(second);
      }
    }
    if (!moved) {
      return;
    }
  }
}

/** True when two laid-out footprints still cover each other. */
export function pinsOverlap(first: PinFootprint, second: PinFootprint): boolean {
  return (
    Math.abs(second.point.x - first.point.x) < first.halfWidth + second.halfWidth &&
    Math.abs(second.point.y - first.point.y) < first.halfHeight + second.halfHeight
  );
}

/** How far a pin ended up from the place it marks. */
export function pinOffset(footprint: PinFootprint): number {
  return Math.hypot(footprint.point.x - footprint.base.x, footprint.point.y - footprint.base.y);
}

function hasAnyOverlap(footprints: PinFootprint[]): boolean {
  for (let a = 0; a < footprints.length; a += 1) {
    for (let b = a + 1; b < footprints.length; b += 1) {
      if (pinsOverlap(footprints[a] as PinFootprint, footprints[b] as PinFootprint)) {
        return true;
      }
    }
  }
  return false;
}

export type PinInput = {
  key: string;
  base: PixelPoint;
  priceTag: string;
};

export type PinLayout = {
  footprints: PinFootprint[];
  /** True when the price pills had to be dropped to fit the pins in. */
  compact: boolean;
};

function toFootprint(input: PinInput, compact: boolean): PinFootprint {
  return {
    key: input.key,
    base: { ...input.base },
    point: { ...input.base },
    halfWidth: (compact ? PIN_CIRCLE_SIZE : estimatePinWidth(input.priceTag)) / 2,
    halfHeight: (compact ? PIN_CIRCLE_SIZE : PIN_STACK_HEIGHT) / 2,
  };
}

/**
 * Lays out a guide's pins for one zoom level.
 *
 * Wide views are the hard case: zoomed out to the whole 20 km radius, a dozen
 * places from one old town cannot all show a price pill without either covering
 * each other or drifting halfway across the map. So the pass is tried twice —
 * full pins with their prices first, and if that cannot be resolved, price-free
 * pin heads, which need a quarter of the room. The price is still one hover or
 * one list row away.
 */
export function layoutPins(inputs: PinInput[]): PinLayout {
  const full = inputs.map((input) => toFootprint(input, false));
  separatePins(full);
  if (!hasAnyOverlap(full)) {
    return { footprints: full, compact: false };
  }

  const compact = inputs.map((input) => toFootprint(input, true));
  separatePins(compact);
  return { footprints: compact, compact: true };
}
